import classNames from 'classnames';
import React, { useMemo } from 'react';

import {
  DerivativesMarginMode,
  DerivativesOrderSide,
  DerivativesOrderStatus,
  DerivativesOrderType,
  DerivativesPositionCloseType,
  DerivativesPositionSide,
} from 'common/api/autogenerated/trading-gateway';

import { AssetIcons } from 'common/components';

import { useI18n } from 'common/hooks';

import { closeTypeClassNames } from 'views/derivatives/orders/classNames';
import SideLabelComponent from 'views/derivatives/orders/common/SideLabel.component';

import './index.css';

const PairInfoComponent: React.FC<
  React.PropsWithChildren<{
    pairName: string;
    // TODO: Fix this the next time the file is edited.

    isRow: boolean;
    isColumn: boolean;
    marginMode?: DerivativesMarginMode;
    leverage?: number;
    side?: DerivativesOrderSide | DerivativesPositionSide;
    type?: DerivativesOrderType;
    closeType?: DerivativesPositionCloseType | DerivativesOrderStatus;
    closeTypeLabel?: string;
    className?: string;
  }>
> = (props) => {
  const { translateToText } = useI18n();

  const pairClassNames = classNames({
    'derivative-order-pair-info': true,
    'derivative-order-pair-info--column': props.isColumn,
    'derivative-order-pair-info--row': props.isRow,
    [`${props.className}`]: props.className,
    [`${props.className}--column`]: props.isColumn && props.className,
  });

  const pairDataClassNames = classNames({
    'derivative-order-pair-info__data': true,
    'derivative-order-pair-info__data--column': props.isColumn,
  });

  const pairIconClassNames = classNames({
    'derivative-order-pair-info-icon': true,
    [`${props.className}-icon`]: props.className,
  });

  const pairDataRowClassNames = classNames({
    'derivative-order-pair-info__data-row': true,
    'derivative-order-pair-info__data-row--column': props.isColumn,
  });

  const tradeModeLabel = useMemo(
    () =>
      translateToText(
        props.marginMode === DerivativesMarginMode.CROSS
          ? 'mobile:derivative:open-orders:card:trade-mode:cross'
          : 'mobile:derivative:open-orders:card:trade-mode:isolated',
      ),
    [props.marginMode, translateToText],
  );

  const closeTypeClassName = useMemo(() => closeTypeClassNames(props.closeType), [props.closeType]);

  return (
    <div className={pairClassNames}>
      <AssetIcons name={props.pairName} className={pairIconClassNames} />
      <div className={pairDataClassNames}>
        <div className={pairDataRowClassNames}>
          <span className='derivative-order-pair-info__name'>{props.pairName}</span>
        </div>
        <div className={pairDataRowClassNames}>
          <SideLabelComponent side={props.side} isColumn={props.isColumn} type={props.type} />
          {props.marginMode && <span className='derivative-order-pair-info--margin'>{tradeModeLabel}</span>}
          {props.leverage && <span className='derivative-order-pair-info--leverage'>{props.leverage}X</span>}
          {props.closeType && props.closeTypeLabel && props.isColumn ? (
            <span className={closeTypeClassName}>{props.closeTypeLabel}</span>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default React.memo(PairInfoComponent);
